// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useReportingMap } from 'sonata-platform-components';
import { CartographySettings, HeatmapSettings } from 'sonata-platform-components/lib/types/types/reportingMap.types';
import { BoxV2 } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import CartographyHeatmapLayer from '@/components/Map/components/ReportingLayers/CartographyHeatmapLayer';
import EventsLayer from '@/components/Map/components/ReportingLayers/EventsLayer';
import HeatmapLayer from '@/components/Map/components/ReportingLayers/HeatmapLayer';
import ScreensStoresLayer from '@/components/Map/components/ReportingLayers/ScreensStoresLayer';
import { reportAdvancedFilterSetMaps } from '@/store/actions/reportingAdvancedFilter.actions';
import { getCountriesWithEvents, getStaticMode } from '@/store/selectors/reporting.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { ControlPanel } from '@/ts-modules/reporting/Map/ControlPanel';
import { LocationsLayer } from '@/ts-modules/reporting/Map/Layers';
import { MapContainerStyled, MapSectionStyled } from '@/ts-modules/reporting/Map/MapContainer/MapContainer.styled';
import { MapView } from '@/ts-modules/reporting/Map/MapView';
import { RankingBottomPanel } from '@/ts-modules/reporting/Map/RankingBottomPanel';
import { SectionHeader } from '@/ts-modules/reporting/SectionHeader';
import { FootfallAttributionMapSelectors } from '../MapControls/FootfallAttributionMapSelectors';
import { VariableSettings } from '../MapControls/MapInnerControls/VariableSettings';

type Props = {
  isFootfallAttrMap?: boolean;
  title: string;
};

type State = {
  isMounted: boolean;
  rankingIsExpanded: boolean;
};

function MapContainer({ isFootfallAttrMap = false, title }: Props) {
  const dispatch = useDispatch();
  const {
    getMap,
    state: { maps }
  } = useReportingMap();

  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const staticMode = useSelector(getStaticMode);
  const countries: string[] = useSelector(getCountriesWithEvents);

  const [{ isMounted, rankingIsExpanded }, setState] = useState<State>({
    isMounted: false,
    rankingIsExpanded: maps.some(({ type }) => type === 'cartography')
  });

  const rankingPermissions = checkUserResourcePermissions('reportingMapRanking', user, userRoles);

  const getFilterComponent = (id: string) => {
    return isFootfallAttrMap ? <FootfallAttributionMapSelectors mapId={id} /> : <VariableSettings mapId={id} />;
  };

  const getFootfallLayers = (mapId: string) => {
    const map = getMap({ id: mapId });
    if (map) {
      const { type } = map;
      switch (type) {
        case 'cartography':
          return [
            {
              Component: CartographyHeatmapLayer,
              params: {
                countries,
                mapId
              }
            }
          ];
        case 'locations':
          return [
            {
              Component: LocationsLayer,
              params: {
                mapId
              }
            }
          ];
        case 'screens':
          return [
            {
              Component: ScreensStoresLayer,
              params: {
                mapId,
                type: 'screens'
              }
            }
          ];
        case 'stores':
          return [
            {
              Component: ScreensStoresLayer,
              params: {
                mapId,
                type: 'stores'
              }
            }
          ];
        default:
          return [];
      }
    }
    return [];
  };
  const getMapLayers = (mapId: string) => {
    const reportingLayers = [];
    const map = getMap({ id: mapId });
    if (map) {
      const { legend, settings, type } = map;

      const mapSettingsByType = settings[type];
      const metrics = legend.filter(item => item.visible).map(item => item.event);
      if (type === 'single') {
        reportingLayers.push({
          Component: EventsLayer,
          params: {
            mapId,
            metrics,
            selectedGeoEvent: metrics.find(m => ['impressions', 'playouts'].includes(m))
          }
        });
      }
      if (!staticMode && type === 'heatmap') {
        reportingLayers.unshift({
          Component: HeatmapLayer,
          params: {
            intensity: (mapSettingsByType as HeatmapSettings).intensity,
            mapId,
            metric: metrics[0],
            radius: (mapSettingsByType as HeatmapSettings).radius
          }
        });
      }
      if (type === 'cartography' && metrics.length) {
        reportingLayers.push({
          Component: CartographyHeatmapLayer,
          params: {
            countries: (mapSettingsByType as CartographySettings).countries,
            mapId,
            shape: (mapSettingsByType as CartographySettings).level
          }
        });
      }
      if (type === 'screens') {
        reportingLayers.push({
          Component: ScreensStoresLayer,
          params: {
            mapId,
            type: 'screens'
          }
        });
      }
    }
    return reportingLayers;
  };

  const getLayersType = (id: string) => {
    return isFootfallAttrMap ? getFootfallLayers(id) : getMapLayers(id);
  };

  useEffect(() => {
    setState(prev => ({
      ...prev,
      isMounted: true
    }));
    dispatch(reportAdvancedFilterSetMaps('maps', maps[0].id));
    return () => {
      setState(prev => ({
        ...prev,
        isMounted: false
      }));
    };
  }, []);

  return (
    <MapSectionStyled>
      <SectionHeader hideAdvancedFilters section={isFootfallAttrMap ? 'footfallAttributionInsights' : 'maps'}>
        <div className="h2 fontRegular reportingMapsSection__title">{title}</div>
      </SectionHeader>
      <MapContainerStyled className="mapContainer" id="mapContainer-box" isFootfallAttrMap={isFootfallAttrMap}>
        <BoxV2.Body className="mapContainer__boxBody" padding="0">
          <ControlPanel className="mapContainer__controlPanel" isFootfallAttrMap={isFootfallAttrMap} />
          <div className="mapContainer__mapsContainer">
            <div className="mapContainer__maps">
              {isMounted &&
                maps.map(({ id }) => (
                  <MapView
                    filtersComponent={getFilterComponent(id)}
                    isFootfallAttrMap={isFootfallAttrMap}
                    key={`mapView_${id}`}
                    layers={getLayersType(id)}
                    mapId={id}
                    multipleMap={maps.length > 1}
                  />
                ))}
            </div>
            {rankingPermissions && maps.length > 1 && (
              <RankingBottomPanel isFootfallAttrMap={isFootfallAttrMap} rankingIsExpanded={rankingIsExpanded} />
            )}
          </div>
        </BoxV2.Body>
      </MapContainerStyled>
    </MapSectionStyled>
  );
}

export default MapContainer;
